/*
 * <copyright company="Argenbright Innovations Lab">
 *        copyright (c) Argenbright Innovations Lab, an Argenbright Holdings Company.  All rights reserved.
 * </copyright>
 */
import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { Box, Divider, FormControl, FormControlLabel, Radio, RadioGroup, Typography } from '@mui/material';

import Modal from '../../../Jobs/Utilities/Shared/Components/Modal';
import SearchVendor from '../../../Jobs/Components/AddEditOneTimeJob/SearchVendor';
import { buttonStyles } from '../../../Jobs/Components/AddEditOneTimeJob/AddEditOneTimeJob.Style';
import { FieldNames, IVendor } from '../../../Jobs/Models/AddEditOneTimeJob.Model';
import JobCreateApiService from '../../../Jobs/Services/JobCreateService';
import CreateBidServices from '../../Services/CreateBidServices';

import { BidStatus, DraftStatus, YesNoType } from '../../../../API';
import { ExecutionType, EstimationType } from '../../../../Shared/Constants/App';
import { getStoredCustomerDetails } from '../../../../Shared/Utilities/utils';
import AsyncStorageKeys from '../../../../Shared/Constants/StorageKeys';
import SnackbarMessage from '../../../../Shared/Components/Common/SnackbarMessage/SnackbarMessage';

interface IMarketplaceExecutionTypeModalProps {
  open: boolean;
  onClose: () => void;
  jobRow: any;
}

const MarketplaceExecutionTypeModal = ({ open, onClose, jobRow }: IMarketplaceExecutionTypeModalProps): JSX.Element => {
  const { t } = useTranslation(['oneTimeJob', 'vendor', 'assignJob']);
  const navigate = useNavigate();

  const { radioButton, radioButtonText, headingText, divider } = buttonStyles;

  // Local layout overrides (scoped to this modal only) so labels stay on a single line
  // and the options stack vertically, matching the peazy-app reference design.
  const verticalRadioGroup = {
    display: 'flex',
    flexDirection: 'column' as const,
    gap: '1rem',
  };
  const singleLineLabel = {
    ...radioButtonText,
    whiteSpace: 'nowrap' as const,
  };
  const vendorDetailsWrapper = {
    display: 'flex',
    flexDirection: 'column' as const,
    alignItems: 'stretch',
    gap: '1rem',
    marginTop: '1.5rem',
    marginBottom: '2rem',
    // SearchVendor's container carries a large top margin, and its input row a large
    // bottom margin, by default; collapse both here so the search box sits snugly under
    // the "Vendor details" heading.
    '& > div': {
      marginTop: 0,
    },
    '& > div > div': {
      marginBottom: 0,
    },
  };

  const [selectedExecution, setSelectedExecution] = useState<string>(ExecutionType.Self);
  const [searchedVendors, setSearchedVendors] = useState<IVendor[]>([
    { vendorId: '', vendorName: '', index: 0, email: '' },
  ]);
  const [isLoading, setIsLoading] = useState(false);
  const [snackbarOpen, setSnackbarOpen] = useState(false);
  const [snackbarMessage, setSnackbarMessage] = useState('');

  const isSubVendor = selectedExecution === ExecutionType.SubVendor;

  const handleExecutionChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setSelectedExecution(event.target.value);
  };

  const onVendorSelect = (vendors: IVendor[]) => {
    setSearchedVendors(vendors);
  };

  const handleApiError = (errorMessage?: string) => {
    setSnackbarMessage(errorMessage || t('vendor:errorOccurred'));
    setSnackbarOpen(true);
    setIsLoading(false);
  };

  // Create (or resolve) the vendor's bid on the marketplace job up front, so the Create Bid page has a
  // valid bidId immediately — the vendor quote attachment/estimations attach to it. Mirrors the mobile
  // app, which creates this "sub" bid before the estimations screen.
  const resolveMarketplaceBidId = async (jobId: string, vendorId: string): Promise<string> => {
    const existingBids = await JobCreateApiService.jobBidResponse(jobId, vendorId);
    const existingBidId =
      existingBids?.data?.find((bid: any) => bid?.vendorId === vendorId)?.bidId ?? existingBids?.data?.[0]?.bidId ?? '';
    if (existingBidId) {
      return existingBidId;
    }
    const bidResponse = await CreateBidServices.addJobBid(jobId, vendorId, {
      bidCreatedFromSample: '',
      bidCreationDate: '',
      bidEstimationType: 'Easy',
      dataStatus: BidStatus.A,
      bidVersionCreatedDate: '',
      submittedBy: '',
      bidTemplate: '',
      bidVersion: 0,
      bidSubmissionDate: '',
      bidName: jobRow?.jobName ?? '',
      finalQuote: 0,
      profitMargin: 0,
      totalCost: 0,
      isFlatMargin: YesNoType.N,
      draftStatus: DraftStatus.Y,
    });
    return bidResponse?.data?.bidId ?? '';
  };

  const onContinue = async () => {
    try {
      setIsLoading(true);
      const { customerId: loggedInVendorId = '' } = getStoredCustomerDetails() || {};
      const jobCustomerId = jobRow?.customerId ?? '';
      const jobId = jobRow?.jobId ?? '';

      const vendorsPayload = isSubVendor
        ? [{ mdVendorType: 'SubVendor', vendorId: searchedVendors?.[0]?.vendorId }]
        : [{ mdVendorType: 'SelfPerform', vendorId: loggedInVendorId }];

      const addJobVendorsResponse = await JobCreateApiService.addJobVendors(jobCustomerId, jobId, vendorsPayload);

      if (addJobVendorsResponse?.errors?.length) {
        handleApiError(addJobVendorsResponse.errors[0]?.message);
        return;
      }

      localStorage.removeItem(AsyncStorageKeys.currentJob);

      let jobDetails: any = { ...jobRow, isVendorBid: true };

      if (isSubVendor) {
        const marketplaceBidId = await resolveMarketplaceBidId(jobId, loggedInVendorId);
        if (!marketplaceBidId) {
          handleApiError();
          return;
        }

        jobDetails = {
          ...jobRow,
          isVendorBid: true,
          // Explicit flag so BidNow can scope its local-sales "estimate from vendor"
          // handling to this flow alone, without affecting any other bid flow.
          isMarketplaceSubVendorBid: true,
          executionType: ExecutionType.SubVendor,
          estimationType: EstimationType.EstimateOutsidePeazy,
          bidId: marketplaceBidId,
          jobBids: [{ bidId: marketplaceBidId }],
        };
      }

      navigate('/create-bid', { state: { jobDetails, jobId } });
    } catch (error) {
      handleApiError();
    } finally {
      setIsLoading(false);
    }
  };

  const renderBody = (
    <Box>
      <Box sx={{ marginBottom: '1.5rem' }}>
        <Typography sx={headingText}>{t('oneTimeJob:executionType')}</Typography>
        <FormControl component="fieldset" sx={{ width: '100%' }}>
          <RadioGroup
            sx={verticalRadioGroup}
            aria-label="execution-type"
            name="execution-type-radio-button"
            value={selectedExecution}
            onChange={handleExecutionChange}>
            <Box>
              <FormControlLabel
                value={ExecutionType.Self}
                control={<Radio sx={{ ...radioButton }} />}
                label={t('oneTimeJob:selfPerform')}
                sx={singleLineLabel}
              />
            </Box>
            <Box>
              <FormControlLabel
                value={ExecutionType.SubVendor}
                control={<Radio sx={{ ...radioButton }} />}
                label={t('oneTimeJob:subContractor')}
                sx={singleLineLabel}
              />
            </Box>
          </RadioGroup>
        </FormControl>
      </Box>

      {isSubVendor && (
        <>
          <Divider sx={divider} />
          <Box sx={{ marginTop: '1.5rem', marginBottom: '1.5rem' }}>
            <Typography sx={headingText}>{t('oneTimeJob:estimationType')}</Typography>
            <FormControl component="fieldset" sx={{ width: '100%' }}>
              <RadioGroup
                sx={verticalRadioGroup}
                aria-label="estimation-type"
                name="estimation-type-radio-button"
                value={EstimationType.EstimateOutsidePeazy}>
                <Box>
                  <FormControlLabel
                    value={EstimationType.EstimateOutsidePeazy}
                    control={<Radio sx={{ ...radioButton }} />}
                    label={t('oneTimeJob:estimateFromVendor')}
                    sx={singleLineLabel}
                  />
                </Box>
              </RadioGroup>
            </FormControl>
          </Box>

          <Divider sx={divider} />
          <Box sx={vendorDetailsWrapper}>
            <Typography sx={headingText}>{t('oneTimeJob:vendorDetails')}</Typography>
            <SearchVendor
              fieldName={FieldNames.searchedVendors}
              selectedVendors={searchedVendors}
              onVendorSelect={onVendorSelect}
              isMulti={false}
              inPopup={true}
            />
          </Box>
        </>
      )}
    </Box>
  );

  return (
    <>
      <Modal
        open={open}
        onClose={onClose}
        primaryButtonLoading={isLoading}
        primaryButtonDisabling={isSubVendor ? !searchedVendors?.[0]?.vendorId : false}
        primaryButtonLabel={t('assignJob:continue')}
        primaryButtonVisible={true}
        secondaryButtonLabel={t('assignJob:cancel')}
        secondaryButtonVisible
        renderHeader={null}
        isRemoveDivider={true}
        renderBody={renderBody}
        primaryAction={onContinue}
        secondaryAction={onClose}
      />
      <SnackbarMessage
        open={snackbarOpen}
        successMessage={snackbarMessage}
        errorMessage={snackbarMessage}
        severity="error"
        onClose={() => setSnackbarOpen(false)}
      />
    </>
  );
};

export default MarketplaceExecutionTypeModal;
